import type { ApolloClient, NormalizedCacheObject } from "@apollo/client"
import { ReactNode, createContext, useContext } from "react"
import type {
  BlocksContentType,
  BlocksContextType,
  BlocksOpenSupportRequest,
  BlocksTemplateVariablesType,
  ContentCompletionType,
  CopiedBlockClipType
} from "../types"

export type BlocksSaveAudience = {
  // Context name for the audience (e.g.: the Course name or Cohort name)
  name?: string
  // Number of Cohorts sharing the Source document, when known.
  cohortCount?: number
  // Whether this document's audience is shared across Cohorts.
  shared?: boolean
}

export type BlocksConfigContextType = {
  // School/space ID; used for saving blocks.
  accountID: number | string
  // Context key; used for locating and validating blocks content.
  contextKey: string
  // Alert parent when blocks are saved.
  onSave?: ({ content }: { content: BlocksContentType }) => void | Promise<void>
  // Metadata about which "audience" (e.g.: Cohorts) the current Blocks document is shared with.
  // This allows Blocks to be ignorant of Pathwright Cohorts, etc.
  // Displays the audience in ContentPublishAction.tsx
  saveAudience?: BlocksSaveAudience
  documentScope?: "source" | "effective"
  reviewResponses?: boolean
  // Inherited Cohort content stays published/read-only until customization.
  cohortCustomization?: { cohortName: string; sourceStepUrl?: string }

  // User's ID; used for saving blocks.
  userID?: number | string

  // Is Blocks acting in viewer or editor mode (VIEW is default).
  mode?: "VIEW" | "EDIT"

  // A consumer-supplied transport; alternatively provide an explicit endpoint.
  graphQLClient?: ApolloClient<NormalizedCacheObject>
  authToken?: string | null
  graphQLEndpoint?: string

  // Allows passing blocks from context instead of server
  initialBlocks?: {
    blocksContext: BlocksContextType
    content: BlocksContentType
  }
  // Is the content being viewed in standalone/offline mode
  offline?: boolean
  // Disable user input (ex: Teacher reviewing Learner answers)
  disableInput?: boolean
  // Custom message to display when input is disabled
  inputDisabledMessage?: string
  // Trigger a "Reset" on all submitted blocks; hack for when a parent step is reset.
  needsForcedUnsubmit?: boolean
  // This is to allow force updating blocks from the "outside" (offline app etc.)
  externalForcedUpdateDatetime?: string
  // Implement step setting that hides answer feedback.
  hideAssessmentFeedback?: boolean
  /**
   * When true, Question blocks always require explicit Submit (no paragraph
   * autocomplete). Used by legacy assessment_editor hybrid UI.
   */
  forceQuestionSubmit?: boolean
  // Clipboard interface: copy a block to the clipboard.
  copyBlock?: (blockClip: CopiedBlockClipType | null) => void
  // Clipboard interface: paste a block from the clipboard clip.
  copiedBlock?: CopiedBlockClipType
  // Alert parent when blocks content has been initialized.
  onDataLoaded?: ({
    content,
    blocksContext
  }: {
    content: BlocksContentType
    blocksContext: BlocksContextType
  }) => void
  // Alert parent when progress block completion is updated.
  renderLoading?: (message?: string) => ReactNode
  onProgress?: (completion: ContentCompletionType) => void
  // Respond to reload request (for error handling)
  onReload?: () => void
  // Debounce rate for syncing blocks
  changeSaveInterval?: number
  // Template variable data to be used in blocks text
  templateVariables?: BlocksTemplateVariablesType
  // Allows passing a theme to Blocks; currently implemented for pathwright-ui
  theme?: Record<string, any>
  // Current user has superuser permissions
  isSuperuser?: boolean

  getUserByID?: (userID: number) => Promise<any>

  allowDownloadableVideos?: boolean

  // Currently unused
  template?: string
  isLocal?: boolean
  firebaseConfig?: {
    url?: string | null
    client_api_key?: string | null
    firebaseAuthToken?: string | null
  } | null

  // Open support UI (e.g., Intercom) or fallback handler
  openSupportRequest?: BlocksOpenSupportRequest

  // Preview mode: changes will be made to local state only; no server sync
  isPreviewMode?: boolean
  // Pilot flag for an authorized, read-only Source Step draft preview.
  liveDraftPreview?: boolean
}

export type BlocksConfigPropsType = BlocksConfigContextType & {
  children?: ReactNode
}

const initalState: BlocksConfigContextType = {
  mode: "VIEW",
  userID: 1,
  accountID: 3,
  contextKey: "/blocks",
  onSave: ({ content }: { content: BlocksContentType }) => {},
  changeSaveInterval: 2000
}

const BlocksConfigContext = createContext<BlocksConfigContextType>(initalState)

export const useBlocksConfig = (): BlocksConfigContextType =>
  useContext(BlocksConfigContext)

export const BlocksConfigProvider = ({
  children,
  onSave = () => {},
  changeSaveInterval = 2000,
  mode = "VIEW",
  ...passValue
}: BlocksConfigPropsType) => {
  return (
    <BlocksConfigContext.Provider
      value={{ ...passValue, onSave, changeSaveInterval, mode }}
    >
      {children}
    </BlocksConfigContext.Provider>
  )
}

export default BlocksConfigProvider
